import React, { useEffect, useCallback } from 'react';
import { ShopPage } from '../components';
import { useShopStore } from '../../../redux/shop';
import { useCustomerStore } from '../../../redux/customers';
import { useToastStore } from '../../../redux/toast';
import {Product} from "../../../models";

export const ShopPageContainer: React.FC = () => {
    const shopStore = useShopStore();
    const { products, isFetching, hasFetched, getProducts, addToCart, cartCount, errorMessage, clearErrors } = shopStore;
    const customerStore = useCustomerStore();
    const { activeCustomer } = customerStore;
    const toastStore = useToastStore();

    const isEventsOnly = products.length > 0 && products.every(p => p.type === 'event_package');

    useEffect(() => {
        if (activeCustomer && !hasFetched && !isFetching && !errorMessage) {
            getProducts(activeCustomer.id)
                .catch(() => {
                    // Rejection handled by the shop store
                });
        }
    }, [activeCustomer, getProducts, hasFetched, isFetching, errorMessage]);

    useEffect(() => {
        if (errorMessage) {
            toastStore.showError({ message: errorMessage }).then(() => {
                clearErrors();
            });
        }
    }, [errorMessage, toastStore, clearErrors]);

    const handleAddToCart = useCallback((product: Product, learnerId: number | null) => {
        const cartItemId = `${product.productKey}:${learnerId ?? 'guest'}`;
        addToCart({
            id: cartItemId,
            type: product.type,
            productId: product.packageId,
            packageId: product.packageId,
            eventPackageId: product.eventPackageId,
            shoppableType: product.shoppableType,
            shoppableId: product.shoppableId,
            itemId: product.itemId,
            product,
            learnerId,
            quantity: 1,
        });

        toastStore.showSuccess({ message: `${product.name} added to cart`});
    }, [addToCart, toastStore]);

    const handleRefresh = useCallback((event: CustomEvent) => {
        if (activeCustomer) {
            getProducts(activeCustomer.id)
                .catch(() => {
                    // Rejection handled by the shop store
                })
                .finally(() => {
                    (event.target as HTMLIonRefresherElement).complete();
                });
        }
    }, [activeCustomer, getProducts]);

    return (
        <ShopPage
            customer={activeCustomer}
            products={products}
            isFetching={isFetching}
            hasFetched={hasFetched}
            cartCount={cartCount}
            onAddToCart={handleAddToCart}
            doRefresh={handleRefresh}
            isEventsOnly={isEventsOnly}
        />
    );
};
